import { createSelector } from '@reduxjs/toolkit';
import getInboxesFromSelectedInboxes from 'store/helpers/getInboxesFromSelectedInboxes';
import { AllInboxesStatusLatestUpdateDate } from 'store/models/InboxesStatusLatestUpdateDate';
import { AllInboxesStatusTime } from 'store/models/InboxesStatusTime';
import SearchFilter from 'store/models/SearchFilter';
import { Statuses } from 'store/models/Statuses';
import { Ticket } from 'store/models/Ticket';
import TicketTypeHelper from 'store/models/TicketTypeHelper';
import { LoadWorkdeskTicketsParams } from 'store/models/workdesk-tickets/workdesk-tickets.interface';
import {
  WorkdeskFilterStoreState,
  WorkdeskFilterWithPrevStoreState,
} from 'store/models/WorkdeskFilter';
import { WorkdeskTickets } from 'store/models/workdesk-tickets/WorkdeskTickets';
import { workdeskTicketsSelector } from './workdesk-tickets.selector';
import { numTicketsStateSelector } from './num-tickets.selector';

export const workdeskFilterWithPrevSelector = (
  state,
): WorkdeskFilterWithPrevStoreState => state.workdeskFilter;

export const workdeskFilterSelector = createSelector(
  workdeskFilterWithPrevSelector,
  (storeState): WorkdeskFilterStoreState => storeState.cur,
);

export const workdeskFilterSelectedStatusSelector = createSelector(
  workdeskFilterSelector,
  (storeState) => storeState.selectedStatus,
);

export const selectedStatusAndRelatedSelector = createSelector(
  workdeskFilterSelectedStatusSelector,
  (selectedStatus) => Statuses.getWSRequestStatuses(selectedStatus),
);

export const relatedStatusTicketTypesSelector = createSelector(
  workdeskFilterSelectedStatusSelector,
  (selectedStatus) =>
    Statuses.getWSRequestStatusTicketTypes(selectedStatus),
);

export const relatedStatusTicketTypesObjSelector = createSelector(
  workdeskFilterSelectedStatusSelector,
  (selectedStatus) =>
    Statuses.getWSRequestStatusTicketTypesObj(selectedStatus),
);

export const workdeskFilterSearchValuesSelector = createSelector(
  workdeskFilterSelector,
  (wdFilter) => wdFilter.searchValues,
);

export const workdeskFilterHasAnySearchValuesSelector = createSelector(
  workdeskFilterSearchValuesSelector,
  (searchValues) =>
    !searchValues
      ? false
      : SearchFilter.hasAnySearchValues(searchValues),
);

export const selectedInboxesListSelector = createSelector(
  workdeskFilterSelector,
  (wdFilter) => {
    const { selectedInboxes } = wdFilter;
    console.debug(
      `selectedInboxes: `,
      selectedInboxes,
      'wdFilter',
      wdFilter,
    );
    return getInboxesFromSelectedInboxes(selectedInboxes);
  },
);

export const loadParamsIgnoreHasLoadedSelector = createSelector(
  workdeskFilterWithPrevSelector,
  (workdeskFilterWithPrev): LoadWorkdeskTicketsParams => {
    const params = WorkdeskTickets.workdeskFilterToLoadParams(
      workdeskFilterWithPrev,
    );
    return params as LoadWorkdeskTicketsParams;
  },
);

export const hadCalledApiSelector = createSelector(
  workdeskFilterWithPrevSelector,
  (storeState) => storeState.hadCalledApi,
);

export const selectedTicketTypeSelector = createSelector(
  workdeskFilterSelector,
  (curFilter) => curFilter.selectedTicketType,
);

export const showAssignUnassignButtonSelector = createSelector(
  workdeskFilterSelector,
  ({
    selectedStatus,
    selectedTicketType,
  }): {
    assign: boolean;
    unassign: boolean;
  } => {
    const si = TicketTypeHelper.getSiFromTicketType(
      selectedTicketType,
    );
    console.debug(`showAssignUnassignButtonSelector si: ${si}`);
    return {
      assign: Ticket.canAssign(selectedStatus, si, true),
      unassign: Ticket.canUnassign(selectedStatus),
    };
  },
);

export const showMarkSiNonSiSelector = createSelector(
  workdeskFilterSelector,
  ({
    selectedStatus,
    selectedTicketType,
  }): { markSi: boolean; markNonSi: boolean } => {
    const si = TicketTypeHelper.getSiFromTicketType(
      selectedTicketType,
    );
    return {
      markSi: Statuses.canMarkSi(selectedStatus, si),
      markNonSi: Statuses.canMarkNonSi(selectedStatus, si),
    };
  },
);

export const canSelectTicketSelector = createSelector(
  workdeskFilterSelector,
  ({ selectedStatus }) => {
    return Statuses.canSelect(selectedStatus);
  },
);

export const canOpenTicketSelector = createSelector(
  workdeskFilterSelector,
  ({ selectedStatus }) => {
    return Statuses.isReadyToViewStatus(selectedStatus);
  },
);
/**
 * filter only the related ones
 */
export const statusAndRelatedLastestUpdateDateSelector = createSelector(
  workdeskTicketsSelector,
  selectedInboxesListSelector,
  relatedStatusTicketTypesObjSelector,
  (wdTickets, inboxes, related) =>
    new AllInboxesStatusLatestUpdateDate(
      wdTickets.allInboxesStatusLatestUpdateDate,
    ).getStatusLatestUpdateDate(inboxes, related),
);

// TODO: considering create a data model for num ticket
export const selectedInboxesnumTicketsStateSelector = createSelector(
  numTicketsStateSelector,
  selectedInboxesListSelector,
  (
    { numTicket },
    inboxesList,
  ):
    | {
        [address: string]: {
          SI: number;
          NON_SI: number;
        };
      }
    | undefined => {
    if (!numTicket || !inboxesList) return undefined;
    const statusCount = Object.keys(numTicket)
      .filter((address: string) => inboxesList.includes(address))
      .reduce((totalMap, address: string) => {
        const thisStatusCount = numTicket[address];
        totalMap = Object.entries<{
          SI: number;
          NON_SI: number;
        }>(thisStatusCount).reduce((acc, [status, countObject]) => {
          const { SI, NON_SI } = countObject;
          return {
            ...acc,
            [status]:
              status in totalMap
                ? {
                    SI: acc[status].SI + SI,
                    NON_SI: acc[status].NON_SI + NON_SI,
                  }
                : {
                    SI,
                    NON_SI,
                  },
          };
        }, totalMap);
        return totalMap;
      }, {});
    return statusCount;
  },
);

export const statusCountForTicketTypeSelector = createSelector(
  selectedInboxesnumTicketsStateSelector,
  workdeskFilterSelector,
  (
    statusCount,
    { selectedTicketType },
  ): { [status: string]: number } | undefined => {
    if (!statusCount) return statusCount;
    const result = Object.keys(statusCount).reduce(
      (acc, status) => ({
        ...acc,
        [status]: statusCount[status][selectedTicketType],
      }),
      {},
    );
    return result;
  },
);
export const pingWsAfterSelector = createSelector(
  (state) => state.websocket.connected,
  workdeskFilterHasAnySearchValuesSelector,
  (connected, hasAnySearchValues) => {
    return connected && !hasAnySearchValues;
  },
);

export const wsCurStatusInboxesLatestReceivedTimeSelector = createSelector(
  (state) => state.websocket.allInboxesStatusLatestReceivedTime,
  workdeskFilterSelectedStatusSelector,
  selectedTicketTypeSelector,
  selectedInboxesListSelector,
  (allInboxesStatusLatestReceivedTime, status, ticketType, inboxes) =>
    new AllInboxesStatusTime(
      allInboxesStatusLatestReceivedTime,
    ).getStatusTimeForStatusTicketType(inboxes, status, ticketType),
);

export const wsCurStatusInboxesLatestReceivedTimeWithDataSelector = createSelector(
  (state) =>
    state.websocket.allInboxesStatusLatestReceivedTimeWithData,
  workdeskFilterSelectedStatusSelector,
  selectedTicketTypeSelector,
  selectedInboxesListSelector,
  (allTime, status, ticketType, inboxes) =>
    new AllInboxesStatusTime(
      allTime,
    ).getStatusTimeForStatusTicketType(inboxes, status, ticketType),
);
